// Formatting utilities for input fields with live formatting

// Get local date in YYYY-MM-DD format without timezone conversion
export const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Convert date string or Date object to local YYYY-MM-DD format (for display)
export const toLocalDateString = (dateInput) => {
  if (!dateInput) return '';
  let date;
  if (typeof dateInput === 'string') {
    // If it's already a date string (YYYY-MM-DD), return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      return dateInput;
    }
    // If ISO-like string (has time/zone), strip to date part to avoid TZ offset
    const isoMatch = dateInput.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (isoMatch) {
      return isoMatch[1];
    }
    date = new Date(dateInput);
  } else {
    date = dateInput;
  }
  
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatNumberWithCommas = (value) => {
  if (!value && value !== 0) return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

export const formatCurrencyDisplay = (value) => {
  if (!value && value !== 0) return '$0';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value;
  if (isNaN(num)) return '$0';
  return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

// Live-typing currency formatter that PRESERVES cents as the user types.
// Keeps a trailing "." and up to 2 decimal places so values like "$35,317.40"
// can actually be entered (the integer-only formatter above drops them).
export const formatCurrencyInput = (value) => {
  if (value === null || value === undefined) return '';
  // Strip $, commas, and anything that isn't a digit or a dot.
  let s = String(value).replace(/[^0-9.]/g, '');
  if (s === '') return '';
  // Collapse any extra decimal points down to the first one.
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  const [intPart, decPart] = s.split('.');
  const intNum = intPart === '' ? 0 : parseInt(intPart, 10);
  const intStr = isNaN(intNum) ? '0' : intNum.toLocaleString('en-US');
  if (firstDot === -1) return '$' + intStr;
  return '$' + intStr + '.' + (decPart || '').slice(0, 2);
};

// Display a stored numeric amount, showing cents only when the value has them.
export const formatCurrencyStored = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/[$,]/g, '')) : value;
  if (isNaN(num)) return '';
  const hasCents = Math.round(Math.abs(num) * 100) % 100 !== 0;
  return '$' + num.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

export const getCurrencyValue = (displayValue) => {
  if (!displayValue && displayValue !== 0) return 0;
  const num = parseFloat(displayValue.replace(/[$,]/g, ''));
  return isNaN(num) ? 0 : num;
};

export const getNumberValue = (displayValue) => {
  if (!displayValue && displayValue !== 0) return 0;
  const num = parseFloat(displayValue.replace(/,/g, ''));
  return isNaN(num) ? 0 : num;
};
